/**
 * UserAvatar — single source of truth for how users appear across the
 * platform. Shows the uploaded `avatar_data_url` if present, otherwise
 * renders a coloured circle with the user's initials.
 *
 * Used from: dashboard profile card, leaderboard rows, admin snapshot
 * dialog, community feed, top nav, chat, etc. If the user prop is
 * absent, we fall back to a neutral placeholder.
 */
export default function UserAvatar({ user, size = 40, className = "", testid }) {
    const url = user?.avatar_data_url;
    const name = user?.full_name || user?.email || "?";
    const initials = name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("") || "?";

    // Deterministic hue from the name so the same user is always the same colour.
    const hue = [...name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 0);

    const style = {
        width: size,
        height: size,
        fontSize: Math.max(10, size * 0.4),
        background: url ? undefined : `hsl(${hue}, 55%, 45%)`,
    };

    return (
        <div
            data-testid={testid || "user-avatar"}
            className={`inline-flex items-center justify-center rounded-full text-white font-bold shrink-0 overflow-hidden ${className}`}
            style={style}
            aria-label={`Avatar of ${name}`}
        >
            {url
                ? <img src={url} alt="" className="w-full h-full object-cover" />
                : <span>{initials}</span>}
        </div>
    );
}
